import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthProvider";
import { toast } from "sonner";
import { useEffect } from "react";

/**
 * Wraps admin-only routes. Behaviour:
 *   • While `ready` is false → renders a lightweight "Checking access…"
 *     placeholder (no flicker).
 *   • Not signed in → redirects to `/` and triggers a toast prompting sign-in.
 *   • Signed in but not admin → redirects to `/` with a "not authorised" toast.
 *   • Signed in as admin → renders `children`.
 */
export const ProtectedAdminRoute = ({ children }) => {
  const { user, isAdmin, ready, signIn } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      toast.error("Sign in with Google to access admin");
      // Kick the popup so the user can complete auth in-place after redirect
      signIn();
    } else if (!isAdmin) {
      toast.error(`${user.email} is not the admin`);
    }
  }, [ready, user, isAdmin, signIn]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Checking access…</p>
      </div>
    );
  }
  if (!user || !isAdmin) {
    return <Navigate to="/" replace state={{ from: loc.pathname }} />;
  }
  return children;
};

export default ProtectedAdminRoute;
